import React, { Suspense } from 'react';
import {
  __federation_method_getRemote,
  __federation_method_setRemote,
} from 'virtual:__federation__';
import {
  registerWorkspaceHostAuth,
  registerWorkspaceHostAuthBridge,
} from '../../auth/registerWorkspaceHostAuth';

interface WorkspaceDashboardProps {
  isActive?: boolean;
  regionalUiHost?: string;
  Config?: Record<string, unknown>;
}

interface WorkspaceRemoteModule {
  WorkspacePage?: React.ComponentType<WorkspaceDashboardProps>;
  default?: React.ComponentType<WorkspaceDashboardProps> | WorkspaceRemoteModule;
}

const WorkspaceDashboardRemote = React.lazy(async () => {
  const [module] = await Promise.all([
    // @ts-expect-error kciWorkspace is a Module Federation remote defined in vite.config.ts
    import('kciWorkspace/WorkspacePage'),
    registerWorkspaceHostAuth(),
  ]);
  // WorkspacePage is a named export with no default, so the federation
  // runtime's default-interop wrapping nests the raw module under
  // `.default` instead of leaving it at the top level — check both shapes.
  const component = module.WorkspacePage ?? module.default?.WorkspacePage ?? module.default;

  if (!component) {
    throw new Error('WorkspacePage export not found in kciWorkspace/WorkspacePage');
  }

  return { default: component as React.ComponentType<WorkspaceDashboardProps> };
});

const regionalWorkspaceRemotes = new Map<
  string,
  Promise<React.ComponentType<WorkspaceDashboardProps>>
>();

function getRemoteEntryUrl(regionalUiHost: string): string {
  const host = regionalUiHost.trim();
  if (!host) {
    throw new Error('Workspace remote host is empty');
  }

  const hasHttpProtocol = /^https?:\/\//i.test(host);
  const isLocalHost = /^(localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i.test(host);
  const url = new URL(
    hasHttpProtocol
      ? host
      : `${isLocalHost ? 'http' : 'https'}://${host}`,
  );

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error(`Unsupported workspace remote protocol: ${url.protocol}`);
  }

  if (url.pathname.endsWith('/remoteEntry.js')) {
    return url.toString();
  }

  url.pathname = `${url.pathname.replace(/\/$/, '')}/workspace/assets/remoteEntry.js`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

function hashRemoteUrl(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function loadRegionalWorkspaceRemote(regionalUiHost: string) {
  const cacheKey = regionalUiHost.trim();
  const cached = regionalWorkspaceRemotes.get(cacheKey);
  if (cached) {
    return cached;
  }

  const remote = Promise.resolve().then(async () => {
    const remoteEntryUrl = getRemoteEntryUrl(regionalUiHost);
    const remoteName = `kciWorkspace_${hashRemoteUrl(remoteEntryUrl)}`;
    __federation_method_setRemote(remoteName, {
      url: remoteEntryUrl,
      format: 'esm',
      from: 'vite',
    });
    const module = await __federation_method_getRemote<WorkspaceRemoteModule>(remoteName, './WorkspacePage');
    try {
      const authBridge = await __federation_method_getRemote<unknown>(remoteName, './authBridge');
      registerWorkspaceHostAuthBridge(authBridge);
    } catch (error) {
      console.error(
        '[kci-core] Failed to register host auth with regional workspace remote; it will fall back to its own login flow.',
        error,
      );
    }
    const component = module.WorkspacePage ??
      (typeof module.default === 'object' ? module.default.WorkspacePage : module.default);

    if (!component) {
      throw new Error(`WorkspacePage export not found at ${remoteEntryUrl}`);
    }

    return component;
  });

  regionalWorkspaceRemotes.set(cacheKey, remote);
  return remote;
}

interface RegionalWorkspaceRemoteProps {
  regionalUiHost: string;
  Config?: Record<string, unknown>;
  isActive: boolean;
}

interface RegionalWorkspaceRemoteState {
  component: React.ComponentType<WorkspaceDashboardProps> | null;
  error: unknown;
}

class RegionalWorkspaceRemote extends React.PureComponent<
  RegionalWorkspaceRemoteProps,
  RegionalWorkspaceRemoteState
> {
  state: RegionalWorkspaceRemoteState = { component: null, error: null };
  private requestVersion = 0;

  componentDidMount() {
    this.loadRemote();
  }

  componentDidUpdate(previousProps: RegionalWorkspaceRemoteProps) {
    if (previousProps.regionalUiHost !== this.props.regionalUiHost) {
      this.setState({ component: null, error: null });
      this.loadRemote();
    }
  }

  componentWillUnmount() {
    this.requestVersion += 1;
  }

  private loadRemote() {
    const requestVersion = ++this.requestVersion;
    void loadRegionalWorkspaceRemote(this.props.regionalUiHost)
      .then((component) => {
        if (requestVersion === this.requestVersion) {
          this.setState({ component, error: null });
        }
      })
      .catch((error: unknown) => {
        if (requestVersion === this.requestVersion) {
          this.setState({ component: null, error });
        }
      });
  }

  render() {
    if (this.state.error) {
      throw this.state.error;
    }

    const LoadedWorkspacePage = this.state.component;
    if (!LoadedWorkspacePage) {
      return <div className="body-content">Loading workspace dashboard...</div>;
    }

    return <LoadedWorkspacePage isActive={this.props.isActive} Config={this.props.Config} />;
  }
}

export const RemoteWorkspaceDashboard = React.memo(function RemoteWorkspaceDashboard({
  isActive = true,
  regionalUiHost,
  Config,
}: WorkspaceDashboardProps) {
  if (regionalUiHost) {
    return (
      <RegionalWorkspaceRemote
        regionalUiHost={regionalUiHost}
        isActive={isActive}
        Config={Config}
      />
    );
  }

  return (
    <Suspense fallback={<div className="body-content">Loading workspace dashboard...</div>}>
      <WorkspaceDashboardRemote isActive={isActive} Config={Config} />
    </Suspense>
  );
});

export default RemoteWorkspaceDashboard;